import React, { useContext, useEffect, useState } from "react";
import { usePopupLock } from "../../context/PopupLockContext";
import { UserContext } from "../../context/UserContext";
import UpdateProfileModal from "../../component/Navbar/UpdateProfileModal";

export default function CallPopup({ isOpen, onClose }) {
  const { user } = useContext(UserContext);
  const { openLock, closeLock } = usePopupLock();

  console.log("🔥 USER OBJECT:", user);

  const identifier = user?.identifier || "";
  const isEmailLogin = identifier.includes("@");
  const isLoggedIn = !!user;

  const resolvedPhone =
    user?.phoneNo ||
    user?.phone ||
    user?.phoneNumber ||
    (!isEmailLogin ? user?.identifier : "") ||
    "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNo: "",
  });

  const [loading, setLoading] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isAutoSubmitted, setIsAutoSubmitted] = useState(false);

  // ✅ API Base URL from env
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  /* ================= RESET + OPEN ================= */
  useEffect(() => {
    if (!isOpen) {
      setForm({ name: "", email: "", phoneNo: "" });
      setIsAutoSubmitted(false);
      closeLock("call");
      return;
    }

    setForm({
      name: user?.name || user?.fullName || user?.userName || "",
      email: isEmailLogin ? user?.identifier || "" : user?.email || "",
      phoneNo: resolvedPhone,
    });

    openLock("call");
  }, [isOpen, user]);

  /* ================= AUTO SUBMIT ================= */
  useEffect(() => {
    if (!isLoggedIn || isAutoSubmitted || !isOpen) return;

    const autoSubmit = async () => {
      try {
        const payload = {
          id: 0,
          userId: user?.userId || 0,
          name: user?.name || user?.fullName || user?.userName || "",
          email: isEmailLogin
            ? user?.identifier || ""
            : user?.email || "",
          phoneNo: resolvedPhone,
          dateForCall: new Date().toISOString(),
          status: true,
        };

        console.log("📤 AUTO PAYLOAD:", payload);

        // ✅ Using env URL
        const response = await fetch(
          `${API_BASE_URL}Customer/InsertCallUs`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        const data = await response.json();

        console.log("✅ AUTO RESPONSE:", data);

        if (response.ok) {
          console.log("🎉 AUTO SUBMIT SUCCESS");
        }

        setIsAutoSubmitted(true);
      } catch (err) {
        console.log("❌ AUTO ERROR:", err);
      }
    };

    autoSubmit();
  }, [isOpen, isLoggedIn, isAutoSubmitted, user, resolvedPhone, API_BASE_URL]);

  if (!isOpen) return null;

  /* ================= CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= CLOSE ================= */
  const handleClose = () => {
    closeLock("call");
    onClose();
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (!form.phoneNo) return alert("Phone is required");
    if (!form.name) return alert("Name is required");

    try {
      setLoading(true);

      const payload = {
        id: 0,
        userId: 0,
        name: form.name,
        email: form.email,
        phoneNo: form.phoneNo,
        dateForCall: new Date().toISOString(),
        status: true,
      };

      console.log("📤 MANUAL PAYLOAD:", payload);

      // ✅ Using env URL
      const res = await fetch(
        `${API_BASE_URL}Customer/InsertCallUs`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      console.log("📡 RESPONSE:", data);

      if (res.ok) {
        alert("Request submitted successfully ✅");
        handleClose();
      } else {
        alert("Something went wrong ❌");
      }
    } catch (err) {
      console.log(err);
      alert("Something went wrong ❌");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
      />

      {/* MODAL */}
      <div className="relative bg-white rounded-2xl shadow-xl p-6 w-[90%] max-w-md z-10">

        {/* LOGGED IN UI */}
        {isLoggedIn ? (
          <div className="text-center py-5 space-y-3">
            <h3 className="text-lg font-bold text-red-600">
              THANK YOU FOR CHOOSING US.
            </h3>

            <p className="text-gray-700 font-medium">
              SOON OUR REPRESENTATIVE WILL CONNECT YOU.
            </p>

            <p className="text-sm text-gray-500">
              (In case of emergency connect us on : 81783 99885)
            </p>
          </div>
        ) : (
          /* GUEST FORM */
          <div className="space-y-3">
            <input
              type="text"
              name="name"
              placeholder="Enter Name"
              value={form.name}
              onChange={handleChange}
              className="w-full border p-2 rounded-lg"
            />

            <input
              type="email"
              name="email"
              placeholder="Enter Email"
              value={form.email}
              onChange={handleChange}
              className="w-full border p-2 rounded-lg"
            />

            <input
              type="tel"
              name="phoneNo"
              placeholder="Enter Phone Number"
              value={form.phoneNo}
              maxLength={10}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setForm((prev) => ({ ...prev, phoneNo: value }));
              }}
              className="w-full border p-2 rounded-lg"
            />
          </div>
        )}

        {/* BUTTONS */}
        <div className="flex gap-3 mt-5">
          {!isLoggedIn && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 text-white py-2.5 rounded-xl"
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          )}

          <button
            onClick={handleClose}
            className="flex-1 border py-2.5 rounded-xl"
          >
            {isLoggedIn ? "Close" : "Cancel"}
          </button>
        </div>
      </div>

      {/* UPDATE MODAL */}
      <UpdateProfileModal
        show={isUpdateOpen}
        onClose={() => setIsUpdateOpen(false)}
        onSuccess={(data) => {
          console.log("PROFILE UPDATED:", data);
        }}
      />
    </div>
  );
}